import {SafeHtml} from "@angular/platform-browser";

import {UtilsService} from "framework/services/utils.service";
import {IControlOptionNode} from "framework/ui/control-option-node.interface";
import {IconDefinition, ILazyTreeNode} from "framework/ui/dropdowns/filterable-tree.component";
import {DecodeHtmlEntitiesPipe} from "framework/ui/formatting/string-format.pipe";

interface IControlOptionCounts
{
    optionCount: number;
    selectableOptionCount: number;
}

export class ControlOption<T> implements IControlOptionNode<T>
{
    private static decodePipe = new DecodeHtmlEntitiesPipe();

    id: T;
    label: string;
    icon?: IconDefinition;
    tooltipOverride: string;
    safeLabel?: SafeHtml;
    isDivider: boolean;
    lazyChildren: ILazyTreeNode<T>[];
    descendantCount?: number;
    selectableDescendantCount?: number;
    searchString?: string;

    get isDecorative(): boolean
    {
        return this.isDivider;
    }

    get additionalWidth(): number
    {
        let additionalPx = 0;
        if (this.tooltipOverride) additionalPx += 26; // 20px width + 6px margin
        if (this.icon) additionalPx += 28;
        return additionalPx;
    }

    constructor(id?: T,
                label?: string,
                public children: ControlOption<T>[] = [],
                public disableSelection: boolean    = false,
                public isGroup: boolean             = false,
                public decodeLabel: boolean         = false)
    {
        this.label           = decodeLabel ? ControlOption.decodePipe.transform(label) : label;
        this.id              = id;
        this.children ||= [];
        this.tooltipOverride = null;
    }

    set hasChildren(hasChildren: boolean)
    {
        this.m_hasChildren = hasChildren;
    }

    get hasChildren(): boolean
    {
        return this.children?.length > 0 || this.m_hasChildren;
    }

    private m_hasChildren: boolean;

    static getLabels<T, O extends IControlOptionNode<T>>(ids: T[],
                                                         options: O[]): string
    {
        let text: string = "";
        if (ids && ids.length > 0)
        {
            // compose string of selected filter items
            for (let id of ids)
            {
                let itemText = ControlOption.getLabel(id, options);
                if (itemText) text += itemText + ", ";
            }

            // remove last comma
            if (text && text.length)
            {
                text = text.substring(0, text.length - 2);
            }
        }
        return text;
    }

    static getLabel<T, O extends IControlOptionNode<T>>(id: T,
                                                        options: O[],
                                                        withHierarchicalContext: boolean = false): string
    {
        if (id != null && options && options.length)
        {
            for (let option of options)
            {
                if (option.id == id) return option.label;

                if (option.children?.length)
                {
                    let candidateName = ControlOption.getLabel(id, option.children, withHierarchicalContext);
                    if (candidateName) return withHierarchicalContext ? option.label + " -- " + candidateName : candidateName;
                }
            }
        }

        return null;
    }

    static getDivider<T>(label?: string): ControlOption<T>
    {
        let newOption       = new ControlOption(null, label || "---------------------");
        newOption.isDivider = true;
        return newOption;
    }

    static sortByLabel(options: ControlOption<any>[],
                       ascending: boolean,
                       recursive?: boolean): void
    {
        if (options?.length > 1)
        {
            if (recursive)
            {
                for (const option of options) ControlOption.sortByLabel(option.children, ascending, recursive);
            }

            options.sort((a,
                          b) => UtilsService.compareStrings(a.label, b.label, ascending));
        }
    }

    static mapRecursive<T, O>(options: ControlOption<T>[],
                              fn: (option: ControlOption<T>) => O): O[]
    {
        const results: O[] = [];
        UtilsService.forEachRecursive(options, (option: ControlOption<T>) => results.push(fn(option)));
        return results;
    }

    static filterByLeaves<I, T extends ControlOption<I>>(options: T[],
                                                         fn: (option: T) => boolean): T[]
    {
        return UtilsService.filterInPlace(options, fn, true);
    }

    static deepCopy<I, T extends ControlOption<I>>(options: T[]): T[]
    {
        return UtilsService.filter(options, () => true, true);
    }

    static find<T, O extends IControlOptionNode<T>>(options: O[],
                                                    fn: (option: O) => boolean): O
    {
        for (const option of options || [])
        {
            if (fn(option)) return option;
            const matchingDescendant = ControlOption.find(<O[]>option.children, fn);
            if (matchingDescendant) return matchingDescendant;
        }
        return null;
    }

    static findById<I, O extends IControlOptionNode<I>>(id: I,
                                                        options: O[]): O
    {
        return ControlOption.find(options, (option) => option.id == id);
    }

    static matchesId<I, O extends IControlOptionNode<I>>(option: O,
                                                         id: I): boolean
    {
        return !!ControlOption.findById(id, [option]);
    }

    static annotateDescendantCounts<T>(options: IControlOptionNode<T>[]): IControlOptionCounts
    {
        const totals: IControlOptionCounts = {
            optionCount          : 0,
            selectableOptionCount: 0
        };

        if (!options?.length) return totals;

        for (const option of options)
        {
            const counts = ControlOption.annotateDescendantCountsForOption(option);
            totals.optionCount += counts.optionCount;
            totals.selectableOptionCount += counts.selectableOptionCount;
        }

        return totals;
    }

    static countOptions<T>(options: ControlOption<T>[]): number
    {
        if (!options?.length) return 0;

        const hasMissingCounts = options.some((option) => option.descendantCount == null);
        if (hasMissingCounts) return ControlOption.annotateDescendantCounts(options).optionCount;

        let count = 0;
        for (const option of options) count += option.descendantCount + (ControlOption.isRenderableOption(option) ? 1 : 0);
        return count;
    }

    static countSelectableOptions<T>(options: ControlOption<T>[]): number
    {
        if (!options?.length) return 0;

        const hasMissingCounts = options.some((option) => option.selectableDescendantCount == null);
        if (hasMissingCounts)
        {
            return ControlOption.annotateDescendantCounts(options).selectableOptionCount;
        }

        let count = 0;
        for (const option of options) count += option.selectableDescendantCount + (ControlOption.isSelectableOption(option) ? 1 : 0);
        return count;
    }

    private static annotateDescendantCountsForOption<T>(option: IControlOptionNode<T>): IControlOptionCounts
    {
        let counts: IControlOptionCounts = {
            optionCount          : 0,
            selectableOptionCount: 0
        };
        for (const child of option.children || [])
        {
            const childCounts = ControlOption.annotateDescendantCountsForOption(child);
            counts.optionCount += childCounts.optionCount;
            counts.selectableOptionCount += childCounts.selectableOptionCount;
        }

        option.descendantCount           = counts.optionCount;
        option.selectableDescendantCount = counts.selectableOptionCount;

        return {
            optionCount          : counts.optionCount + (ControlOption.isRenderableOption(option) ? 1 : 0),
            selectableOptionCount: counts.selectableOptionCount + (ControlOption.isSelectableOption(option) ? 1 : 0)
        };
    }

    private static isRenderableOption<T>(option: IControlOptionNode<T>): boolean
    {
        return option.id != null && !option.isDecorative;
    }

    private static isSelectableOption<T>(option: IControlOptionNode<T>): boolean
    {
        return ControlOption.isRenderableOption(option) && !option.isGroup && !option.disableSelection;
    }
}
